import { useState, useRef, useEffect } from "react";
import { HelpCircle } from "lucide-react";

/**
 * HelpPopup — a small ? icon that reveals a popover with contextual help.
 * Use:
 *   <HelpPopup title="What goes here?" items={[{label:'STL files', body:'...'}, ...]} />
 * Or:
 *   <HelpPopup body="Free-form help text" />
 */
export default function HelpPopup({ title, body, items, testid = "help-popup", align = "left" }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (!ref.current?.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    return (
        <span ref={ref} className="relative inline-flex items-center align-middle">
            <button
                type="button"
                data-testid={`${testid}-toggle`}
                onClick={() => setOpen((v) => !v)}
                className={`inline-flex items-center justify-center h-5 w-5 rounded-full border border-slate-300 text-slate-500 hover:border-cyan-500 hover:text-cyan-700 transition ${open ? "border-cyan-500 text-cyan-700" : ""}`}
                aria-label="Show help"
            >
                <HelpCircle size={12} />
            </button>
            {open && (
                <div
                    data-testid={testid}
                    className={`absolute z-50 top-7 ${align === "right" ? "end-0" : "start-0"} w-[320px] rounded-xl bg-white border border-slate-200 shadow-xl p-4`}
                >
                    {title && (
                        <div className="font-display text-[14px] tracking-tight text-slate-900 mb-2">{title}</div>
                    )}
                    {body && (
                        <p className="text-[12.5px] text-slate-600 leading-relaxed">{body}</p>
                    )}
                    {items && (
                        <ul className="space-y-2.5">
                            {items.map((it, i) => (
                                <li key={`${it.label || "item"}-${i}`} className="text-[12.5px]">
                                    <div className="font-semibold text-slate-900 mb-0.5">{it.label}</div>
                                    <div className="text-slate-600 leading-relaxed">{it.body}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </span>
    );
}
